import React, { useState, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import axios from 'axios';
import styles from './index.module.scss';
import { selectAll as selectAllLevels } from '../../redux/slices/levels.slice';
import { selectAll as selectAllStyles } from '../../redux/slices/styles.slice';
import { selectAll as selectAllTeachers } from '../../redux/slices/teachers.slice';

const getApiUrl = () =>
    (window._env_ && window._env_.REACT_APP_API_URL) ||
    process.env.REACT_APP_API_URL ||
    'http://localhost:8080';

function AccordionRow({ name, color, classes, hours, children }) {
    const [open, setOpen] = useState(false);
    return (
        <div className={styles.accordionRow}>
            <button
                className={styles.accordionHeader}
                onClick={() => setOpen(o => !o)}
                aria-expanded={open}
            >
                <span className={styles.dot} style={{ background: color }} />
                <span className={styles.accName}>{name}</span>
                <span className={styles.accCount}>{classes} {classes === 1 ? 'class' : 'classes'}</span>
                <svg
                    className={[styles.chevron, open ? styles.chevronOpen : ''].join(' ')}
                    width="12" height="12" viewBox="0 0 24 24"
                    fill="none" stroke="currentColor" strokeWidth="2.5"
                    strokeLinecap="round" strokeLinejoin="round"
                    aria-hidden="true"
                >
                    <polyline points="6 9 12 15 18 9" />
                </svg>
            </button>
            {open && (
                <div className={styles.drillDown}>
                    {children}
                </div>
            )}
        </div>
    );
}

function Badge({ label, color }) {
    const bg = color ? color + '22' : '#eee';
    const text = color || '#555';
    return (
        <span className={styles.badge} style={{ background: bg, color: text, borderColor: color ? color + '55' : '#ccc' }}>
            {label}
        </span>
    );
}

export default function ReportPanel({ board, userId, onClose }) {
    const [loading, setLoading] = useState(true);
    const [weekData, setWeekData] = useState(null);
    const [error, setError] = useState(null);

    const allLevels = useSelector(selectAllLevels);
    const allStyles = useSelector(selectAllStyles);
    const allTeachers = useSelector(selectAllTeachers);

    const levelById = useMemo(() => {
        const map = {};
        allLevels.forEach(l => { map[l.id] = l; });
        return map;
    }, [allLevels]);

    const teacherById = useMemo(() => {
        const map = {};
        allTeachers.forEach(t => { map[t.id] = t; });
        return map;
    }, [allTeachers]);

    const styleByName = useMemo(() => {
        const map = {};
        allStyles.forEach(s => { map[s.styleName] = s; });
        return map;
    }, [allStyles]);

    useEffect(() => {
        if (!board?.id || !userId) return;
        setLoading(true);
        setError(null);
        const base = getApiUrl().replace(/\/$/, '');
        const params = new URLSearchParams({ userId, boardId: board.id });
        axios.get(`${base}/schedules/get-week-schedule?${params}`, {
            headers: { 'Authorization': 'Bearer ' + localStorage.getItem('accessToken') }
        })
            .then(res => setWeekData(res.data))
            .catch(() => setError('Could not load report data.'))
            .finally(() => setLoading(false));
    }, [board?.id, userId]);

    const report = useMemo(() => {
        if (!weekData?.studios) return null;

        const allStickers = weekData.studios.flatMap(s => s.scheduleSticker || []);

        let totalClasses = 0;
        let totalHours = 0;
        const teacherMap = {};
        const levelMap = {};
        const styleMap = {};

        allStickers.forEach(sticker => {
            const length = parseFloat(sticker.length) || 0;
            totalClasses++;
            totalHours += length;

            // --- By Teacher ---
            const tIds = sticker.teachersId || [];
            if (tIds.length > 0) {
                tIds.forEach(tid => {
                    const t = teacherById[tid];
                    if (!t) return;
                    if (!teacherMap[t.id]) {
                        teacherMap[t.id] = { name: t.teacherName, color: t.teacherColor || '#888', classes: 0, hours: 0 };
                    }
                    teacherMap[t.id].classes++;
                    teacherMap[t.id].hours += length;
                });
            } else if ((sticker.teachers || []).length > 0) {
                (sticker.teachers || []).forEach(tName => {
                    const t = allTeachers.find(x => x.teacherName === tName);
                    const color = t?.teacherColor || '#888';
                    const key = `name_${tName}`;
                    if (!teacherMap[key]) {
                        teacherMap[key] = { name: tName, color, classes: 0, hours: 0, unassigned: false };
                    }
                    teacherMap[key].classes++;
                    teacherMap[key].hours += length;
                });
            } else {
                if (!teacherMap['__unassigned__']) {
                    teacherMap['__unassigned__'] = { name: 'Unassigned', color: '#bbb', classes: 0, hours: 0, unassigned: true };
                }
                teacherMap['__unassigned__'].classes++;
                teacherMap['__unassigned__'].hours += length;
            }

            const lIds = sticker.levelsId || [];
            const styleNames = sticker.styles || [];

            // --- By Level → drill down by style ---
            lIds.forEach(lid => {
                const level = levelById[lid];
                if (!level) return;
                if (!levelMap[level.id]) {
                    levelMap[level.id] = { name: level.levelName, color: level.color || '#888', classes: 0, hours: 0, breakdown: {} };
                }
                levelMap[level.id].classes++;
                levelMap[level.id].hours += length;

                if (styleNames.length > 0) {
                    styleNames.forEach(styleName => {
                        const styleInfo = styleByName[styleName];
                        const styleColor = styleInfo?.styleColor || '#888';
                        if (!levelMap[level.id].breakdown[styleName]) {
                            levelMap[level.id].breakdown[styleName] = { badge: styleName, badgeColor: styleColor, classes: 0, hours: 0 };
                        }
                        levelMap[level.id].breakdown[styleName].classes++;
                        levelMap[level.id].breakdown[styleName].hours += length;
                    });
                } else {
                    const key = '__no_style__';
                    if (!levelMap[level.id].breakdown[key]) {
                        levelMap[level.id].breakdown[key] = { badge: 'No style', badgeColor: '#ccc', classes: 0, hours: 0 };
                    }
                    levelMap[level.id].breakdown[key].classes++;
                    levelMap[level.id].breakdown[key].hours += length;
                }
            });

            // --- By Style → drill down by level ---
            styleNames.forEach(styleName => {
                const styleInfo = styleByName[styleName];
                const color = styleInfo?.styleColor || '#888';
                if (!styleMap[styleName]) {
                    styleMap[styleName] = { name: styleName, color, classes: 0, hours: 0, breakdown: {} };
                }
                styleMap[styleName].classes++;
                styleMap[styleName].hours += length;

                if (lIds.length > 0) {
                    lIds.forEach(lid => {
                        const level = levelById[lid];
                        if (!level) return;
                        if (!styleMap[styleName].breakdown[level.id]) {
                            styleMap[styleName].breakdown[level.id] = { badge: level.levelName, badgeColor: level.color || '#888', classes: 0, hours: 0 };
                        }
                        styleMap[styleName].breakdown[level.id].classes++;
                        styleMap[styleName].breakdown[level.id].hours += length;
                    });
                } else {
                    const key = '__no_level__';
                    if (!styleMap[styleName].breakdown[key]) {
                        styleMap[styleName].breakdown[key] = { badge: 'No level', badgeColor: '#ccc', classes: 0, hours: 0 };
                    }
                    styleMap[styleName].breakdown[key].classes++;
                    styleMap[styleName].breakdown[key].hours += length;
                }
            });
        });

        return {
            totalClasses,
            totalHours,
            teachers: Object.values(teacherMap).sort((a, b) => { if (a.unassigned) return 1; if (b.unassigned) return -1; return a.name.localeCompare(b.name); }),
            levels: Object.values(levelMap).sort((a, b) => a.name.localeCompare(b.name)),
            styles: Object.values(styleMap).sort((a, b) => a.name.localeCompare(b.name)),
        };
    }, [weekData, levelById, teacherById, styleByName, allTeachers]);

    const fmtHours = (h) => {
        const rounded = Math.round(h * 4) / 4;
        return rounded % 1 === 0 ? `${rounded}h` : `${rounded.toFixed(2).replace(/0+$/, '')}h`;
    };

    const scheduleName = board?.boardName || board?.label || 'Schedule';

    return (
        <div className={styles.panel}>
            <div className={styles.header}>
                <div>
                    <div className={styles.title}>Schedule report</div>
                    <div className={styles.subtitle}>{scheduleName} · Full week</div>
                </div>
                <button className={styles.closeBtn} onClick={onClose} aria-label="Close report">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" aria-hidden="true">
                        <line x1="18" y1="6" x2="6" y2="18" />
                        <line x1="6" y1="6" x2="18" y2="18" />
                    </svg>
                </button>
            </div>

            <div className={styles.body}>
                {loading && <div className={styles.emptyState}>Loading…</div>}
                {error && <div className={styles.emptyState}>{error}</div>}
                {!loading && !error && report && (
                    <>
                        {/* Headline stats */}
                        <div className={styles.statRow}>
                            <div className={styles.statCard}>
                                <div className={styles.statLabel}>Total classes</div>
                                <div className={styles.statValue}>{report.totalClasses}</div>
                            </div>
                            <div className={styles.statCard}>
                                <div className={styles.statLabel}>Total hours</div>
                                <div className={styles.statValue}>{fmtHours(report.totalHours)}</div>
                            </div>
                        </div>

                        {/* By Teacher */}
                        <div className={styles.section}>
                            <div className={styles.sectionLabel}>By teacher</div>
                            {report.teachers.length === 0 && (
                                <div className={styles.emptyState}>No teachers assigned</div>
                            )}
                            {report.teachers.map((t, i) => (
                                <div key={i} className={styles.teacherRow}>
                                    <div className={styles.avatar} style={{ background: t.color + '22', color: t.color }}>
                                        {t.name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase()}
                                    </div>
                                    <div className={styles.teacherName}>{t.name}</div>
                                    <div className={styles.teacherMeta}>
                                        <div className={styles.teacherClasses}>{t.classes} {t.classes === 1 ? 'class' : 'classes'}</div>
                                        <div className={styles.teacherHours}>{fmtHours(t.hours)}</div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* By Level */}
                        <div className={styles.section}>
                            <div className={styles.sectionLabel}>By level</div>
                            {report.levels.length === 0 && (
                                <div className={styles.emptyState}>No levels assigned</div>
                            )}
                            {report.levels.map((level, i) => (
                                <AccordionRow key={i} name={level.name} color={level.color} classes={level.classes} hours={level.hours}>
                                    {Object.values(level.breakdown).sort((a, b) => a.badge.localeCompare(b.badge)).map((row, j) => (
                                        <div key={j} className={styles.drillRow}>
                                            <Badge label={row.badge} color={row.badgeColor} />
                                            <span className={styles.drillCount}>{row.classes} {row.classes === 1 ? 'class' : 'classes'} · {fmtHours(row.hours)}</span>
                                        </div>
                                    ))}
                                </AccordionRow>
                            ))}
                        </div>

                        {/* By Style */}
                        <div className={styles.section}>
                            <div className={styles.sectionLabel}>By style</div>
                            {report.styles.length === 0 && (
                                <div className={styles.emptyState}>No styles assigned</div>
                            )}
                            {report.styles.map((style, i) => (
                                <AccordionRow key={i} name={style.name} color={style.color} classes={style.classes} hours={style.hours}>
                                    {Object.values(style.breakdown).sort((a, b) => a.badge.localeCompare(b.badge)).map((row, j) => (
                                        <div key={j} className={styles.drillRow}>
                                            <Badge label={row.badge} color={row.badgeColor} />
                                            <span className={styles.drillCount}>{row.classes} {row.classes === 1 ? 'class' : 'classes'} · {fmtHours(row.hours)}</span>
                                        </div>
                                    ))}
                                </AccordionRow>
                            ))}
                        </div>
                    </>
                )}
                {!loading && !error && report && report.totalClasses === 0 && (
                    <div className={styles.emptyState}>No classes scheduled this week</div>
                )}
            </div>
        </div>
    );
}
